import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  RequestPeriod,
  SubmissionMember,
  createPeriod,
  getPeriodStatus,
  listPeriods,
  updatePeriod,
  updateTargets,
  deletePeriod,
} from '../services/shiftRequestService';
import { getUsers } from '../services/userService';
import { User } from '../types/user';

/**
 * 希望シフトの受付（管理者）。
 *
 * ## ⚠️ 未提出者一覧を通知より先に作っている
 *
 * キンログは**従業員のメールアドレスが任意**なので、メールで届かない人がいる。
 * 「システムが本人を追う」より「**店長が誰に声を掛けるか分かる**」方が
 * 10人規模では回る（設計 §5-5-3）。だから未提出者を上に並べる。
 */

const ymd = (d: Date) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

/** ISO文字列を datetime-local が読める「端末のローカル時刻」へ落とす */
const toLocalInput = (iso: string) =>
  new Date(new Date(iso).getTime() - new Date().getTimezoneOffset() * 60000).toISOString().slice(0, 16);

/** 来月の1日〜末日。受付は「来月ぶん」を作るのがほとんど */
const nextMonthRange = () => {
  const now = new Date();
  const from = new Date(now.getFullYear(), now.getMonth() + 1, 1);
  const to = new Date(now.getFullYear(), now.getMonth() + 2, 0);
  return { from: ymd(from), to: ymd(to), name: `${from.getFullYear()}年${from.getMonth() + 1}月分` };
};

const ShiftRequestAdminPage: React.FC = () => {
  const navigate = useNavigate();
  const [periods, setPeriods] = useState<RequestPeriod[]>([]);
  const [selected, setSelected] = useState<number | null>(null);
  const [period, setPeriod] = useState<RequestPeriod | null>(null);
  const [members, setMembers] = useState<SubmissionMember[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [planBlocked, setPlanBlocked] = useState(false);
  const [creating, setCreating] = useState(false);
  const [staff, setStaff] = useState<User[]>([]);
  /** 受付を作るときの対象者。**既定はシフト制の人だけ**（固定制も選べる） */
  const [picked, setPicked] = useState<Set<number>>(new Set());
  const [editingTargets, setEditingTargets] = useState(false);
  const [editingPeriod, setEditingPeriod] = useState(false);
  const [edit, setEdit] = useState({ name: '', dateFrom: '', dateTo: '', deadlineAt: '' });
  /** 期間を変えたときに、提出済みの人へ出し直してもらうか（既定は求めない） */
  const [askResubmit, setAskResubmit] = useState(false);
  /** 「受付を直す」で再提出の許可を触ったか。null は「触っていない」 */
  const [editAllow, setEditAllow] = useState<boolean | null>(null);
  /** 同じく「締切と同時に締め切る」を触ったか */
  const [editCloseAtDeadline, setEditCloseAtDeadline] = useState<boolean | null>(null);
  const [form, setForm] = useState(() => {
    const r = nextMonthRange();
    const deadline = new Date();
    deadline.setDate(deadline.getDate() + 7);
    deadline.setHours(23, 59, 0, 0);
    /*
     * `closeAtDeadline` の既定は**チェックあり**。締切の意味が素直になる。
     * （DB の既定は FALSE。既存の受付の挙動を変えないため。新規だけここで倒す）
     */
    return {
      name: r.name,
      dateFrom: r.from,
      dateTo: r.to,
      deadlineAt: `${ymd(deadline)}T23:59`,
      allowResubmit: true,
      closeAtDeadline: true,
    };
  });

  const loadPeriods = useCallback(async () => {
    try {
      setError('');
      const ps = await listPeriods();
      setPeriods(ps);
      if (ps.length > 0 && selected === null) setSelected(ps[0].id);
      if (ps.length === 0) setLoading(false);
    } catch (e: any) {
      if (e.response?.data?.code === 'PLAN_REQUIRED') setPlanBlocked(true);
      else setError(e.response?.data?.error || '受付期間の取得に失敗しました');
      setLoading(false);
    }
  }, [selected]);

  useEffect(() => {
    loadPeriods();
  }, [loadPeriods]);

  useEffect(() => {
    getUsers('active')
      .then((u) => {
        setStaff(u);
        // 既定はシフト制のみ。固定制はチェックを外した状態で一覧に出す
        setPicked(new Set(u.filter((x) => (x.work_schedule_type ?? 'shift') === 'shift').map((x) => x.id)));
      })
      .catch(() => setStaff([]));
  }, []);

  const loadStatus = useCallback(async () => {
    if (!selected) return;
    try {
      setLoading(true);
      setError('');
      const r = await getPeriodStatus(selected);
      setPeriod(r.period);
      setMembers(r.members);
    } catch (e: any) {
      if (e.response?.data?.code === 'PLAN_REQUIRED') setPlanBlocked(true);
      else setError(e.response?.data?.error || '提出状況の取得に失敗しました');
    } finally {
      setLoading(false);
    }
  }, [selected]);

  useEffect(() => {
    loadStatus();
  }, [loadStatus]);

  // 受付を切り替えたら編集中の入力は捨てる（前の受付の日付が残ると誤操作になる）
  useEffect(() => {
    setEdit({ name: '', dateFrom: '', dateTo: '', deadlineAt: '' });
    setAskResubmit(false);
    setEditAllow(null);
    setEditCloseAtDeadline(null);
    setEditingPeriod(false);
    setEditingTargets(false);
  }, [selected]);

  const create = async () => {
    try {
      setError('');
      const p = await createPeriod({
        name: form.name,
        dateFrom: form.dateFrom,
        dateTo: form.dateTo,
        // datetime-local はタイムゾーンを持たない。端末のローカル時刻として送る
        deadlineAt: new Date(form.deadlineAt).toISOString(),
        userIds: [...picked],
        allowResubmit: form.allowResubmit,
        closeAtDeadline: form.closeAtDeadline,
      });
      setCreating(false);
      setSelected(p.id);
      await loadPeriods();
    } catch (e: any) {
      setError(e.response?.data?.error || '作成に失敗しました');
    }
  };

  const change = async (patch: {
    name?: string;
    dateFrom?: string;
    dateTo?: string;
    deadlineAt?: string;
    status?: 'open' | 'closed';
    resubmit?: boolean;
    allowResubmit?: boolean;
    closeAtDeadline?: boolean;
  }) => {
    if (!selected) return;
    try {
      setError('');
      await updatePeriod(selected, patch);
      await Promise.all([loadPeriods(), loadStatus()]);
    } catch (e: any) {
      // 期間を狭めると範囲外の希望が消える。件数を見せてから確認を取る
      if (e.response?.data?.code === 'WOULD_DROP_ENTRIES') {
        const n = e.response.data.losingEntries;
        if (!window.confirm(`対象期間の外になる希望が ${n}件 あります。

このまま変更すると、その ${n}件 は削除されます。よろしいですか？`)) {
          return;
        }
        try {
          await updatePeriod(selected, { ...patch, confirm: true });
          await Promise.all([loadPeriods(), loadStatus()]);
          return;
        } catch (e2: any) {
          setError(e2.response?.data?.error || '更新に失敗しました');
          return;
        }
      }
      setError(e.response?.data?.error || '更新に失敗しました');
    }
  };

  /**
   * シフト提出の締め切りを開け閉めする。
   *
   * ⚠️ **締め切ると未提出の人はもう出せない。** 何人残っているかを見せてから確認する
   * （代表判断 2026-09-02）。
   */
  const toggleSubmission = async () => {
    if (!period) return;
    const closing = period.status === 'open';
    if (closing) {
      const yet = members.filter((m) => !m.submittedAt);
      const who = yet.length > 0 ? `

未提出 ${yet.length}人: ${yet.map((m) => m.name).join('、')}` : '';
      if (
        !window.confirm(
          `「${period.name}」のシフト提出を締め切ります。

締め切ると、未提出の人も希望シフトを出せなくなります。${who}

よろしいですか？`
        )
      )
        return;
    }
    await change({ status: closing ? 'closed' : 'open' });
  };

  const remove = async () => {
    if (!selected || !period) return;
    if (!window.confirm(`「${period.name}」を削除します。よろしいですか？`)) return;
    try {
      setError('');
      await deletePeriod(selected);
      setSelected(null);
      setPeriod(null);
      setMembers([]);
      await loadPeriods();
    } catch (e: any) {
      // 提出があるものは消させない。締め切って残す運用へ案内する
      setError(e.response?.data?.error || '削除に失敗しました');
    }
  };

  if (planBlocked) {
    return (
      <div style={s.page}>
        <div style={s.planCard}>
          <h1 style={s.h1}>希望シフトの受付</h1>
          <p style={s.note}>
            シフト管理は<strong>シフト管理付きプラン</strong>でご利用いただけます。
          </p>
          <button onClick={() => navigate('/subscription')} style={s.primaryBtn}>プランを見る</button>
        </div>
      </div>
    );
  }

  const notSubmitted = members.filter((m) => !m.submittedAt);
  const late = members.filter((m) => m.isLate);
  const submittedCount = members.length - notSubmitted.length;
  /** 「受付を直す」で対象期間を実際に触ったか。締切や名前だけの変更では出さない */
  const rangeEdited = Boolean(
    period &&
      ((edit.dateFrom && edit.dateFrom !== String(period.dateFrom).slice(0, 10)) ||
        (edit.dateTo && edit.dateTo !== String(period.dateTo).slice(0, 10)))
  );

  return (
    <div style={s.page}>
      <div style={s.header}>
        <div>
          <h1 style={s.h1}>希望シフトの受付</h1>
          <p style={s.sub}>期間と締切を決めて、提出状況を見ます</p>
        </div>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <button onClick={() => setCreating((v) => !v)} style={s.primaryBtn}>受付を作る</button>
          <button onClick={() => navigate('/shifts')} style={s.backBtn}>シフト表</button>
          <button onClick={() => navigate('/dashboard')} style={s.backBtn}>戻る</button>
        </div>
      </div>

      {error && <div style={s.error}>{error}</div>}

      {creating && (
        <section style={s.card}>
          <h2 style={s.h2}>受付を作る</h2>
          <div style={s.formGrid}>
            <label style={s.field}>
              <span style={s.label}>名前</span>
              <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} style={s.input} />
            </label>
            <label style={s.field}>
              <span style={s.label}>対象期間（開始）</span>
              <input type="date" value={form.dateFrom} onChange={(e) => setForm({ ...form, dateFrom: e.target.value })} style={s.input} />
            </label>
            <label style={s.field}>
              <span style={s.label}>対象期間（終了）</span>
              <input type="date" value={form.dateTo} onChange={(e) => setForm({ ...form, dateTo: e.target.value })} style={s.input} />
            </label>
            <label style={s.field}>
              <span style={s.label}>締切</span>
              <input type="datetime-local" value={form.deadlineAt} onChange={(e) => setForm({ ...form, deadlineAt: e.target.value })} style={s.input} />
            </label>
          </div>

          <label style={s.checkRow}>
            <input
              type="checkbox"
              checked={form.allowResubmit}
              onChange={(e) => setForm({ ...form, allowResubmit: e.target.checked })}
              style={{ marginTop: 3 }}
            />
            <span>期限内の再提出を許可する（従業員は提出期限内であれば、何度でも希望シフトの修正が可能です）</span>
          </label>

          {/*
            締切(時刻)と「提出を止めるスイッチ」を分ける（代表判断 2026-09-02）。
            外した場合は、手で「シフト提出を締め切る」を押すまで出せる。
          */}
          <label style={s.checkRow}>
            <input
              type="checkbox"
              checked={form.closeAtDeadline}
              onChange={(e) => setForm({ ...form, closeAtDeadline: e.target.checked })}
              style={{ marginTop: 3 }}
            />
            <span>
              締切と同時にシフト提出を締め切る（従業員は締切以後、未提出であっても希望シフトの提出ができなくなります）
              <br />
              <span style={s.hint}>
                外すと、締切を過ぎても提出できます。止めるときは「シフト提出を締め切る」を押します。
                <strong>シフトの公開は、提出を締め切ってからになります。</strong>
              </span>
            </span>
          </label>
          <StaffPicker staff={staff} picked={picked} setPicked={setPicked} title="この受付の対象者" />

          <button onClick={create} style={{ ...s.primaryBtn, marginTop: 14 }}>作成する</button>
        </section>
      )}

      {periods.length === 0 && !loading ? (
        <div style={s.card}>
          <p style={s.note}>まだ受付がありません。「受付を作る」から始めてください。</p>
        </div>
      ) : (
        <>
          <select value={selected ?? ''} onChange={(e) => setSelected(Number(e.target.value))} style={{ ...s.input, maxWidth: 320, marginBottom: 14 }}>
            {periods.map((p) => (
              <option key={p.id} value={p.id}>
                {p.name}
                {p.status === 'closed' ? '（締切済み）' : ''}
              </option>
            ))}
          </select>

          {period && (
            <section style={s.card}>
              <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', alignItems: 'center' }}>
                <div style={{ fontSize: 13, lineHeight: 1.8 }}>
                  <div>
                    対象: {String(period.dateFrom).slice(0, 10)} 〜 {String(period.dateTo).slice(0, 10)}
                  </div>
                  <div>締切: {new Date(period.deadlineAt).toLocaleString('ja-JP')}</div>
                  <div>期限内の再提出: {period.allowResubmit ? '許可する' : '許可しない'}</div>
                  <div>
                    シフト提出:{' '}
                    <strong style={{ color: period.submissionClosed ? '#991B1B' : '#166534' }}>
                      {period.submissionClosed ? '締め切り済み' : '受付中'}
                    </strong>
                    {period.closeAtDeadline && <span style={s.hint}>（締切と同時に締め切る）</span>}
                    {period.shiftPublishedAt && <span style={{ ...s.pill, background: '#DCFCE7', color: '#166534', marginLeft: 8 }}>シフト公開済み</span>}
                  </div>
                </div>
                <div style={{ display: 'flex', gap: 8, marginLeft: 'auto', flexWrap: 'wrap' }}>
                  {/*
                    手のスイッチ（代表判断 2026-09-02）。
                    「締切と同時に締め切る」を選んでいる受付には出さない — 時刻で閉じるので、
                    ボタンで開け閉めできると**どちらが効いているのか分からなくなる**。
                  */}
                  {!period.closeAtDeadline && (
                    <button onClick={toggleSubmission} style={s.backBtn}>
                      {period.status === 'open' ? 'シフト提出を締め切る' : 'シフト提出の締め切りを解除する'}
                    </button>
                  )}
                  <button onClick={() => setEditingPeriod((v) => !v)} style={s.backBtn}>
                    {editingPeriod ? '閉じる' : '受付を直す'}
                  </button>
                </div>
              </div>
            </section>
          )}

          {editingPeriod && period && (
            <section style={s.card}>
              <h2 style={s.h2}>受付を直す</h2>
              <div style={s.formGrid}>
                <label style={s.field}>
                  <span style={s.label}>名前</span>
                  <input
                    value={edit.name || period.name}
                    onChange={(e) => setEdit({ ...edit, name: e.target.value })}
                    style={s.input}
                  />
                </label>
                <label style={s.field}>
                  <span style={s.label}>対象期間（開始）</span>
                  <input
                    type="date"
                    value={edit.dateFrom || String(period.dateFrom).slice(0, 10)}
                    onChange={(e) => setEdit({ ...edit, dateFrom: e.target.value })}
                    style={s.input}
                  />
                </label>
                <label style={s.field}>
                  <span style={s.label}>対象期間（終了）</span>
                  <input
                    type="date"
                    value={edit.dateTo || String(period.dateTo).slice(0, 10)}
                    onChange={(e) => setEdit({ ...edit, dateTo: e.target.value })}
                    style={s.input}
                  />
                  <span style={s.hint}>狭めると、範囲外になる希望は削除されます（件数を確認します）</span>
                </label>
                {/*
                  締切はここでしか直せない（代表 2026-09-06）。
                  一覧の横に裸で置いていたときは、フォーカスを外した瞬間に保存されていて
                  「直すつもりが無いのに変わる」事故が起きうる。**開いて・直して・保存する**に揃える。
                */}
                <label style={s.field}>
                  <span style={s.label}>締切</span>
                  <input
                    type="datetime-local"
                    value={edit.deadlineAt || toLocalInput(period.deadlineAt)}
                    onChange={(e) => setEdit({ ...edit, deadlineAt: e.target.value })}
                    style={s.input}
                  />
                </label>
              </div>

              <label style={s.checkRow}>
                <input
                  type="checkbox"
                  checked={editAllow ?? period.allowResubmit}
                  onChange={(e) => setEditAllow(e.target.checked)}
                  style={{ marginTop: 3 }}
                />
                <span>期限内の再提出を許可する（従業員は提出期限内であれば、何度でも希望シフトの修正が可能です）</span>
              </label>
              <label style={s.checkRow}>
                <input
                  type="checkbox"
                  checked={editCloseAtDeadline ?? period.closeAtDeadline}
                  onChange={(e) => setEditCloseAtDeadline(e.target.checked)}
                  style={{ marginTop: 3 }}
                />
                <span>締切と同時にシフト提出を締め切る（従業員は締切以後、未提出であっても希望シフトの提出ができなくなります）</span>
              </label>

              {/*
                期間を変えると、提出済みの人は「何も言っていない日」を抱えたまま
                提出済みになる。出し直してもらうかは組む側が決める（代表 2026-09-02）。
                既定は求めない — 黙って提出を取り消す方が事故が大きい。
              */}
              {rangeEdited && submittedCount > 0 && (
                <label style={{ display: 'flex', gap: 8, alignItems: 'flex-start', marginTop: 14, fontSize: 13, lineHeight: 1.7 }}>
                  <input
                    type="checkbox"
                    checked={askResubmit}
                    onChange={(e) => setAskResubmit(e.target.checked)}
                    style={{ marginTop: 4 }}
                  />
                  <span>
                    提出済みの<strong>{submittedCount}人</strong>に出し直してもらう（全員が「未提出」に戻ります）
                    <br />
                    <span style={s.hint}>
                      入力済みの希望は消えません。増えた日を本人に埋めてもらいたいときに使います。
                    </span>
                  </span>
                </label>
              )}

              <div style={{ display: 'flex', gap: 8, marginTop: 14, flexWrap: 'wrap' }}>
                <button
                  onClick={async () => {
                    await change({
                      name: edit.name || period.name,
                      dateFrom: edit.dateFrom || String(period.dateFrom).slice(0, 10),
                      dateTo: edit.dateTo || String(period.dateTo).slice(0, 10),
                      deadlineAt: new Date(edit.deadlineAt || toLocalInput(period.deadlineAt)).toISOString(),
                      resubmit: askResubmit,
                      allowResubmit: editAllow ?? period.allowResubmit,
                      closeAtDeadline: editCloseAtDeadline ?? period.closeAtDeadline,
                    });
                    setEditingPeriod(false);
                    setEdit({ name: '', dateFrom: '', dateTo: '', deadlineAt: '' });
                    setAskResubmit(false);
                    setEditAllow(null);
                    setEditCloseAtDeadline(null);
                  }}
                  style={s.primaryBtn}
                >
                  保存する
                </button>
                {/* 提出があるものは消せない。押せる状態にしておいて、理由をサーバから返す */}
                <button onClick={remove} style={{ ...s.backBtn, background: '#B45309' }}>この受付を削除</button>
              </div>
              <p style={{ ...s.note, marginTop: 10 }}>
                提出が1件でもある受付は削除できません。使わなくなった受付は「締め切る」で残してください。
              </p>
            </section>
          )}

          {/* 未提出が何人いるかを最初に出す。声を掛ける相手が分かることが目的 */}
          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', margin: '4px 0 14px' }}>
            <span style={{ ...s.pill, background: '#FEE2E2', color: '#991B1B' }}>未提出 {notSubmitted.length}人</span>
            <span style={{ ...s.pill, background: '#DCFCE7', color: '#166534' }}>
              提出済み {members.length - notSubmitted.length}人
            </span>
            {late.length > 0 && (
              <span style={{ ...s.pill, background: '#FEF3C7', color: '#92400E' }}>うち締切後 {late.length}人</span>
            )}
          </div>

          {selected && (
            <div style={{ marginBottom: 12 }}>
              <button
                onClick={() => {
                  // いまの対象者を選択状態にしてから開く
                  setPicked(new Set(members.map((m) => m.userId)));
                  setEditingTargets((v) => !v);
                }}
                style={s.backBtn}
              >
                {editingTargets ? '閉じる' : '対象者を変える'}
              </button>
            </div>
          )}

          {editingTargets && selected && (
            <section style={s.card}>
              <StaffPicker
                staff={staff}
                picked={picked}
                setPicked={setPicked}
                title="対象者"
                hint="外した人は提出状況から消え、本人の提出画面にもこの受付が出なくなります。提出済みの人は外せません。"
                locked={new Set(members.filter((m) => m.submittedAt).map((m) => m.userId))}
              />
              <button
                onClick={async () => {
                  try {
                    setError('');
                    const r = await updateTargets(selected, [...picked]);
                    setEditingTargets(false);
                    await Promise.all([loadPeriods(), loadStatus()]);
                    // 締め切ったあとに足した人だけ開けている。黙って開けない
                    if (r.reopened > 0) {
                      window.alert(
                        `この受付は締め切り済みです。
新しく足した ${r.reopened}人 だけ、希望シフトを出せるようにしました。

本人に提出をお願いしてください（出した時点で締め切りに戻ります）。`
                      );
                    }
                  } catch (e: any) {
                    setError(e.response?.data?.error || '対象者の更新に失敗しました');
                  }
                }}
                style={{ ...s.primaryBtn, marginTop: 14 }}
              >
                対象者を保存
              </button>
            </section>
          )}

          {loading ? (
            <p style={s.note}>読み込み中…</p>
          ) : (
            <div style={{ border: '1px solid #dee2e6', borderRadius: 8, background: '#fff', overflow: 'hidden' }}>
              {members.map((m) => (
                <div key={m.userId} style={s.memberRow}>
                  <span style={{ flex: '1 1 auto', fontWeight: 600 }}>
                    {m.name}
                    {/* 固定制の人が混じっているときに一目で分かるように */}
                    {m.workScheduleType === 'fixed' && (
                      <span style={{ ...s.pill, background: '#F1F5F9', color: '#475569', marginLeft: 8 }}>固定制</span>
                    )}
                  </span>
                  {/*
                    締め切ったあとに足された人だけ、いつ足したかを出す（代表 2026-09-06）。
                    「なぜこの人だけまだ出せるのか」が一覧で読めるようにするため。
                  */}
                  {m.reopenedAt && (
                    <span style={{ ...s.pill, background: '#FEF3C7', color: '#92400E' }}>
                      締切後追加（{new Date(m.reopenedAt).toLocaleString('ja-JP')}）
                    </span>
                  )}
                  {m.submittedAt ? (
                    <>
                      <span style={{ fontSize: 12, color: '#64748B' }}>
                        {new Date(m.submittedAt).toLocaleString('ja-JP')}
                      </span>
                      <span style={{ ...s.pill, background: m.isLate ? '#FEF3C7' : '#DCFCE7', color: m.isLate ? '#92400E' : '#166534' }}>
                        {m.isLate ? '締切後' : '提出済み'}
                      </span>
                    </>
                  ) : (
                    <span style={{ ...s.pill, background: '#FEE2E2', color: '#991B1B' }}>未提出</span>
                  )}
                </div>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
};

/**
 * 対象者を選ぶ。
 *
 * **既定はシフト制の人だけ**だが、固定制の人も一覧には出す。
 * 臨時にシフトへ入ってもらう月があるため（代表 2026-08-29）。
 * 隠してしまうと「なぜあの人が出てこないのか」が分からなくなる。
 */
const StaffPicker: React.FC<{
  staff: User[];
  picked: Set<number>;
  setPicked: (s: Set<number>) => void;
  title: string;
  hint?: string;
  /** 提出済みで**外せない**人。チェックを固定する（サーバも 409 で断る） */
  locked?: Set<number>;
}> = ({ staff, picked, setPicked, title, hint, locked }) => {
  const toggle = (id: number) => {
    if (locked?.has(id)) return;
    const next = new Set(picked);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    setPicked(next);
  };
  const shiftStaff = staff.filter((u) => (u.work_schedule_type ?? 'shift') === 'shift');
  const fixedStaff = staff.filter((u) => u.work_schedule_type === 'fixed');

  const row = (u: User) => {
    const fixed = locked?.has(u.id) ?? false;
    return (
      <label
        key={u.id}
        title={fixed ? '提出済みのため外せません' : undefined}
        style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 13, padding: '4px 0', minWidth: 150, color: fixed ? '#64748B' : undefined }}
      >
        <input type="checkbox" checked={picked.has(u.id)} disabled={fixed} onChange={() => toggle(u.id)} />
        {u.name}
        {fixed && <span style={{ fontSize: 11, color: '#166534' }}>提出済み</span>}
      </label>
    );
  };

  return (
    <div style={{ marginTop: 16 }}>
      <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 4 }}>{title}（{picked.size}人）</div>
      {hint && <div style={{ fontSize: 12, color: '#6c757d', marginBottom: 8 }}>{hint}</div>}

      <div style={{ fontSize: 12, color: '#1D4ED8', fontWeight: 700, marginTop: 8 }}>シフト制</div>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
        {shiftStaff.length === 0 ? <span style={{ fontSize: 13, color: '#94A3B8' }}>いません</span> : shiftStaff.map(row)}
      </div>

      {fixedStaff.length > 0 && (
        <>
          <div style={{ fontSize: 12, color: '#64748B', fontWeight: 700, marginTop: 12 }}>固定制</div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>{fixedStaff.map(row)}</div>
        </>
      )}
    </div>
  );
};

const s: Record<string, React.CSSProperties> = {
  page: { maxWidth: 900, margin: '0 auto', padding: 'clamp(10px, 3vw, 20px)' },
  header: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12, flexWrap: 'wrap', marginBottom: 16 },
  h1: { margin: 0, fontSize: 'clamp(18px, 4vw, 24px)' },
  h2: { margin: '0 0 10px', fontSize: 'clamp(15px, 3.2vw, 17px)' },
  sub: { margin: '4px 0 0', color: '#6c757d', fontSize: 13 },
  card: { background: '#fff', border: '1px solid #dee2e6', borderRadius: 8, padding: 'clamp(12px, 3vw, 20px)', marginBottom: 14 },
  planCard: { background: '#fff', border: '1px solid #dee2e6', borderRadius: 8, padding: 32, textAlign: 'center', maxWidth: 520, margin: '40px auto' },
  note: { color: '#6c757d', fontSize: 13, lineHeight: 1.7, margin: 0 },
  hint: { color: '#94A3B8', fontSize: 12, marginTop: 4 },
  error: { background: '#f8d7da', color: '#721c24', border: '1px solid #f5c6cb', borderRadius: 4, padding: 10, marginBottom: 12 },
  formGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 14 },
  field: { display: 'flex', flexDirection: 'column' },
  label: { fontSize: 13, fontWeight: 700, marginBottom: 6 },
  input: { padding: '8px 12px', border: '1px solid #ced4da', borderRadius: 4, fontSize: 14, width: '100%', boxSizing: 'border-box' },
  primaryBtn: { background: '#2563EB', color: '#fff', border: 'none', borderRadius: 4, padding: '9px 18px', fontSize: 14, fontWeight: 700, cursor: 'pointer' },
  backBtn: { background: '#6c757d', color: '#fff', border: 'none', borderRadius: 4, padding: '8px 14px', fontSize: 13, cursor: 'pointer' },
  pill: { borderRadius: 999, padding: '3px 12px', fontSize: 12, fontWeight: 700, whiteSpace: 'nowrap' },
  checkRow: { display: 'flex', gap: 8, alignItems: 'flex-start', marginTop: 14, fontSize: 13, lineHeight: 1.7 },
  memberRow: { display: 'flex', alignItems: 'center', gap: 12, padding: '11px 14px', borderTop: '1px solid #f1f3f5', flexWrap: 'wrap' },
};

export default ShiftRequestAdminPage;
